import { createContext, useContext, type ReactNode } from 'react'
import { cn } from '../lib/utils'
import { kstMinutesOfDay, kstMonthDayStr, kstTimeStr } from '../lib/kst'
import { Badge } from './ui/badge'

/**
 * ★ 출퇴근 시각을 그리는 유일한 컴포넌트.
 *
 * 이 파일은 앱 전체에서 판정을 아는 **단 하나의 지점**이다.
 *
 * ── 왜 mode 를 prop 으로 받지 않는가 ──────────────────────
 *   prop 으로 받으면 모든 호출부가 org.judgmentMode 를 읽어야 한다.
 *   그러면 "판정을 아는 곳"이 화면 수만큼 늘어나고, 그게 정확히
 *   simpleMode 107곳 분기가 생긴 방식이다. 그래서 context 로 받는다.
 *   화면은 <AttendanceTime at={…} kind="CHECKIN" /> 만 쓴다.
 *
 * ── 불변식 ────────────────────────────────────────────────
 *   서버 응답은 모드와 무관하게 항상 동일하다.  status 는 영원히 NORMAL.
 *   판정은 화면에 그릴 때만 계산한다.            저장하지 않는다.
 *
 * ── 하지 말 것 ────────────────────────────────────────────
 *   ❌ status 컬럼에 판정 결과 저장             → determineStatus 부활
 *   ❌ 서버 응답에 파생 판정 필드 추가           → 서버·엑셀·통계로 분기 번짐
 *   ❌ 날짜를 순회하며 "결근" 판정               → cron 재도입 경로
 *   ❌ 판정에 따른 알림·푸시
 *
 * 결근을 넣지 않는 이유: 지각·조퇴는 "있는 기록"을 기준시각과 비교하는 순수
 * 함수지만, 결근은 "없는 기록"을 판정하는 것이라 날짜 순회가 필요하다. 그건
 * 폐기한 cron 을 되살리는 일이다. 부재 placeholder 오염이 정확히 그 경로였다.
 *
 * 배경 = docs/2026-08-04_BizNomi-SaaS-React-스펙-v0.2.md §3
 * 강제 = scripts/guardrails/check-judgment-mode.sh (가드레일 #2)
 */

export type JudgmentMode = 'STANDARD' | 'FACTUAL'
/**
 * 서버 Prisma enum 과 동일하게 맞춘다 (CHECK_IN 아님).
 *
 * 🔴 OUT·RETURN 이 여기 있는 이유 (2026-09-21).
 *   외출·복귀는 **영구 폐기된 기능**이고 새로 쓰지 않는다. 그런데 옛 DB 에 그 기록이
 *   남아 있고(2026-03~04), DCMC 서버를 열면 그 행이 이 화면으로 온다.
 *   전에는 호출부가 `kind={r.type === 'CHECKOUT' ? 'CHECKOUT' : 'CHECKIN'}` 이라
 *   **외출이 출근으로 판정됐다** — 14시 외출에 「지각」이 붙는다.
 *   판정은 저장되지 않지만 화면에 뜨고, 그게 증거로 읽힌다.
 *   그래서 kind 로 받되 **판정하지 않는다** (아래 isFlagged 첫 줄).
 */
export type AttendanceKind = 'CHECKIN' | 'CHECKOUT' | 'OUT' | 'RETURN'

export interface JudgmentConfig {
  mode: JudgmentMode
  /** 조직 기준 출근 시각 "HH:mm" */
  workStartTime?: string
  /** 조직 기준 퇴근 시각 "HH:mm" */
  workEndTime?: string
  /**
   * 지각 유예 분 — 9시 출근인데 9시 5분까지는 봐주는 회사가 있다.
   * 출처 = WorkPolicy.lateGraceMinutes
   */
  graceMinutes?: number
  /**
   * 조퇴 기준 분 — "퇴근시간 N분 전부터 조퇴로 본다".
   * 30 이면 18:00 퇴근 회사에서 17:29 이전만 조퇴다. 17:59 는 조퇴가 아니다.
   * 출처 = WorkPolicy.earlyLeaveMinutes
   */
  earlyLeaveMinutes?: number
  /**
   * 야간근무 여부 — 퇴근 시각이 다음날인 근무유형 (예: 22:00~06:00).
   * 출처 = WorkType.isNightShift
   */
  isNightShift?: boolean
  /**
   * 기준 시각("기준 09:00")을 함께 보여줄지 — 조직 설정 §4 B #7.
   * 판정 여부와는 무관하다. 표시만 끄는 것이고, 지각 배지 자체는 그대로 뜬다.
   */
  showWorkHours?: boolean
}

/** 기본값은 "판정하지 않음". 조직이 명시적으로 켜야 켜진다 (D23) */
const JudgmentContext = createContext<JudgmentConfig>({ mode: 'FACTUAL' })

export function JudgmentProvider({ value, children }: { value: JudgmentConfig; children: ReactNode }) {
  return <JudgmentContext.Provider value={value}>{children}</JudgmentContext.Provider>
}

/** "HH:mm" → 자정 기준 분. 형식이 깨졌으면 null */
function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

/**
 * 판정 계산 — 순수 함수. 테스트 가능하도록 export 한다.
 * 기준 시각이 없거나 형식이 깨졌으면 판정하지 않는다.
 */
export function isFlagged(at: Date, kind: AttendanceKind, cfg: JudgmentConfig): boolean {
  if (cfg.mode !== 'STANDARD') return false
  /*
   * 🔴 외출·복귀는 판정하지 않는다. 기준 시각과 비교할 대상이 아니다 —
   *    14시 외출은 지각이 아니고, 15시 복귀는 조퇴가 아니다.
   *    폐기된 기능의 옛 기록이라 «판정할 규칙이 없다» 가 정확한 상태다.
   */
  if (kind === 'OUT' || kind === 'RETURN') return false
  const actual = kstMinutesOfDay(at)

  if (kind === 'CHECKIN') {
    const base = cfg.workStartTime ? toMinutes(cfg.workStartTime) : null
    return base !== null && actual > base + (cfg.graceMinutes ?? 0)
  }

  const base = cfg.workEndTime ? toMinutes(cfg.workEndTime) : null
  if (base === null) return false

  // 퇴근시간 1분 전을 조퇴라고 하면 아무도 납득하지 않는다. 회사가 정한 폭만큼 뺀다.
  const cutoff = base - (cfg.earlyLeaveMinutes ?? 0)

  if (!cfg.isNightShift) return actual < cutoff

  /*
   * 야간근무는 퇴근이 다음날이다 (예: 22:00~06:00).
   * 단순 비교하면 정반대가 된다 —
   *   23:00 퇴근(= 한 시간 만에 감)  →  23:00 < 06:00 이 거짓이라 "정상"
   *   05:30 퇴근(= 30분 일찍 감)     →  05:30 < 06:00 이 참이라 "조퇴"  ← 이건 맞음
   * 앞의 케이스를 놓치면 안 된다. 근무가 시작 시각부터 자정을 건너 끝난다고 보고,
   * 퇴근 시각이 그 구간 안에서 기준보다 이른지를 본다.
   *
   * DCMC 야간조가 하루 80~174명이라, 이걸 빼먹으면 표준 모드를 켠 병원에서 바로 터진다.
   */
  const start = cfg.workStartTime ? toMinutes(cfg.workStartTime) : null
  if (start === null) return false
  // 자정 이후(= 퇴근 예정 구간)에 찍혔으면 기준과 그대로 비교
  if (actual <= cutoff) return actual < cutoff
  // 자정 전에 찍혔으면, 출근 시각 이후인 한 전부 이르게 나간 것이다
  return actual >= start
}

export interface AttendanceTimeProps {
  /** 서버가 준 태깅 시각 */
  at: Date
  kind: AttendanceKind
  /** 날짜 병기 — 상대 표기("전일") 금지, 실제 날짜만 (DCMC 14-1) */
  showDate?: boolean
  className?: string
}

export function AttendanceTime({ at, kind, showDate = false, className }: AttendanceTimeProps) {
  const cfg = useContext(JudgmentContext)

  const flagged = isFlagged(at, kind, cfg)
  const base = kind === 'CHECKIN' ? cfg.workStartTime : kind === 'CHECKOUT' ? cfg.workEndTime : undefined

  return (
    <span className={cn('inline-flex flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0.5', className)}>
      {showDate && <span className="text-xs text-slate-400">{kstMonthDayStr(at)}</span>}
      <time
        dateTime={at.toISOString()}
        className={cn('font-semibold tabular-nums', flagged && 'text-[var(--color-warning)]')}
      >
        {kstTimeStr(at)}
      </time>
      {flagged && (
        <>
          {/*
            색만으로 정보를 전달하지 않는다 — 색각 이상 사용자에게도 읽혀야 한다.
            "지각"·"조퇴" 는 화면 라벨일 뿐이며 DB 에는 저장되지 않는다.
            비표준 모드에서는 이 블록 전체가 사라진다.
          */}
          <Badge variant="warning">{kind === 'CHECKIN' ? '지각' : '조퇴'}</Badge>
          {base && cfg.showWorkHours !== false && (
            <span className="text-[11px] text-slate-400">기준 {base}</span>
          )}
        </>
      )}
    </span>
  )
}
